import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Badge,
	Progress,
} from "@/components/primitives";
import {
	ArrowLeft,
	User,
	Calendar,
	TrendingUp,
	Target,
	Clock,
	CheckCircle,
	AlertCircle,
	Mail,
	Activity,
	Eye,
} from "lucide-react";

interface MenteeProfile {
	id: string;
	name: string;
	email: string;
	created_at: string;
}

interface ReflectionSummary {
	id: string;
	title: string;
	created_at: string;
	contributing_factors_count: number;
	signals_count: number;
	corrective_actions_count: number;
	completed_actions_count: number;
	progress_percentage: number;
}

interface MenteeStats {
	total_reflections: number;
	total_actions: number;
	completed_actions: number;
	overall_progress: number;
	last_activity: string | null;
	mentorship_duration: number;
}

export function MenteeDetail() {
	const { menteeId } = useParams<{ menteeId: string }>();
	const { user, profile } = useAuth();
	const navigate = useNavigate();
	const [menteeProfile, setMenteeProfile] = useState<MenteeProfile | null>(
		null,
	);
	const [reflections, setReflections] = useState<ReflectionSummary[]>([]);
	const [stats, setStats] = useState<MenteeStats | null>(null);
	const [loading, setLoading] = useState(true);
	const [hasAccess, setHasAccess] = useState(false);

	useEffect(() => {
		if (profile?.user_type !== "mentor" || !menteeId) {
			window.history.length > 1 ? navigate(-1) : navigate("/mentor");
			return;
		}
		fetchMenteeData();
	}, [menteeId, profile, navigate]);

	const fetchMenteeData = async () => {
		try {
			// Use secure edge function to fetch mentee details
			const { data, error } = await supabase.functions.invoke(
				"fetch-mentee-details",
				{
					body: { menteeId },
				},
			);

			if (error) {
				console.error("Edge function error:", error);
				window.history.length > 1 ? navigate(-1) : navigate("/mentor");
				return;
			}

			console.log("Mentee details fetched successfully:", data);

			setHasAccess(true);
			setMenteeProfile(data.profile);
			setReflections(data.reflections);
			setStats(data.stats);
		} catch (error) {
			console.error("Error fetching mentee data:", error);
			window.history.length > 1 ? navigate(-1) : navigate("/mentor");
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const getProgressStatus = (progress: number) => {
		if (progress >= 80)
			return {
				label: "Excellent",
				variant: "default" as const,
				icon: CheckCircle,
			};
		if (progress >= 60)
			return { label: "Good", variant: "secondary" as const, icon: TrendingUp };
		if (progress >= 40)
			return { label: "Moderate", variant: "secondary" as const, icon: Clock };
		return {
			label: "Needs Support",
			variant: "destructive" as const,
			icon: AlertCircle,
		};
	};

	if (profile?.user_type !== "mentor") {
		return null;
	}

	if (loading) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
				</div>
			</div>
		);
	}

	if (!hasAccess || !menteeProfile || !stats) {
		return (
			<div className="container mx-auto px-4 py-8">
				<Card className="max-w-md mx-auto">
					<CardContent className="text-center py-8">
						<p className="text-muted-foreground">
							Unable to access mentee information.
						</p>
						<Button
							onClick={() =>
								window.history.length > 1 ? navigate(-1) : navigate("/mentor")
							}
							className="mt-4"
							variant="outline"
						>
							Back to Dashboard
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const progressStatus = getProgressStatus(stats.overall_progress);
	const StatusIcon = progressStatus.icon;

	return (
		<div className="container mx-auto px-4 py-8">
			{/* Header */}
			<div className="mb-8">
				<Button
					variant="ghost"
					onClick={() =>
						window.history.length > 1 ? navigate(-1) : navigate("/mentor")
					}
					className="mb-4"
				>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Dashboard
				</Button>

				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold mb-2">Mentee Overview</h1>
						<p className="text-muted-foreground">
							Professional summary and progress tracking
						</p>
					</div>
				</div>
			</div>

			{/* Mentee Profile Card */}
			<Card className="mb-8">
				<CardHeader>
					<div className="flex items-center space-x-4">
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
							<User className="w-8 h-8 text-primary" />
						</div>
						<div className="flex-1">
							<CardTitle className="text-2xl">{menteeProfile.name}</CardTitle>
							<CardDescription className="flex items-center space-x-4 mt-2">
								<span className="flex items-center space-x-1">
									<Mail className="w-4 h-4" />
									<span>{menteeProfile.email}</span>
								</span>
								<span className="flex items-center space-x-1">
									<Calendar className="w-4 h-4" />
									<span>Joined {formatDate(menteeProfile.created_at)}</span>
								</span>
								<span className="flex items-center space-x-1">
									<Activity className="w-4 h-4" />
									<span>{stats.mentorship_duration} days under mentorship</span>
								</span>
							</CardDescription>
						</div>
						<Badge
							variant={progressStatus.variant}
							className="flex items-center space-x-1"
						>
							<StatusIcon className="w-4 h-4" />
							<span>{progressStatus.label}</span>
						</Badge>
					</div>
				</CardHeader>
			</Card>

			{/* Stats Overview */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
				<Card>
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<Target className="w-8 h-8 text-blue-600" />
							<div>
								<p className="text-2xl font-bold">{stats.total_reflections}</p>
								<p className="text-sm text-muted-foreground">
									Total Reflections
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<CheckCircle className="w-8 h-8 text-green-600" />
							<div>
								<p className="text-2xl font-bold">{stats.completed_actions}</p>
								<p className="text-sm text-muted-foreground">
									Actions Completed
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<TrendingUp className="w-8 h-8 text-purple-600" />
							<div>
								<p className="text-2xl font-bold">{stats.overall_progress}%</p>
								<p className="text-sm text-muted-foreground">
									Overall Progress
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center space-x-4">
							<Clock className="w-8 h-8 text-orange-600" />
							<div>
								<p className="text-2xl font-bold">
									{stats.last_activity
										? formatDate(stats.last_activity)
										: "No activity"}
								</p>
								<p className="text-sm text-muted-foreground">Last Reflection</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Overall Progress */}
			<Card className="mb-8">
				<CardHeader>
					<CardTitle>Progress Overview</CardTitle>
					<CardDescription>
						Professional assessment based on reflection activity and action
						completion
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<div className="flex justify-between text-sm mb-2">
								<span>Overall Completion Rate</span>
								<span className="font-medium">{stats.overall_progress}%</span>
							</div>
							<Progress value={stats.overall_progress} className="h-2" />
						</div>
						<p className="text-sm text-muted-foreground">
							This mentee has completed {stats.completed_actions} out of{" "}
							{stats.total_actions} recommended actions across{" "}
							{stats.total_reflections} reflection(s).
							{stats.overall_progress >= 70
								? " Showing excellent engagement and follow-through."
								: stats.overall_progress >= 40
									? " Moderate engagement, consider additional support."
									: " Low engagement, may benefit from additional guidance and check-ins."}
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Reflections Summary */}
			<Card>
				<CardHeader>
					<CardTitle>Reflection Summary</CardTitle>
					<CardDescription>
						Professional overview of reflection activity and progress
					</CardDescription>
				</CardHeader>
				<CardContent>
					{reflections.length > 0 ? (
						<div className="space-y-4">
							{reflections.map((reflection) => (
								<div
									key={reflection.id}
									className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
									onClick={() =>
										navigate(`/mentor/reflection/${reflection.id}`)
									}
								>
									<div className="flex items-center justify-between mb-3">
										<h4 className="font-semibold hover:text-primary transition-colors">
											{reflection.title}
										</h4>
										<div className="flex items-center space-x-2">
											<Badge
												variant={
													reflection.progress_percentage >= 70
														? "default"
														: reflection.progress_percentage >= 40
															? "secondary"
															: "destructive"
												}
											>
												{reflection.progress_percentage}% Complete
											</Badge>
											<span className="text-xs text-muted-foreground">
												{formatDate(reflection.created_at)}
											</span>
											<Button
												variant="ghost"
												size="sm"
												className="text-primary hover:text-primary-foreground"
												onClick={(e) => {
													e.stopPropagation();
													navigate(`/mentor/reflection/${reflection.id}`);
												}}
											>
												<Eye className="w-4 h-4 mr-1" />
												View Details
											</Button>
										</div>
									</div>

									<div className="grid grid-cols-3 gap-4 text-sm">
										<div className="text-center">
											<div className="font-medium">
												{reflection.contributing_factors_count}
											</div>
											<div className="text-muted-foreground">Factors</div>
										</div>
										<div className="text-center">
											<div className="font-medium">
												{reflection.signals_count}
											</div>
											<div className="text-muted-foreground">Signals</div>
										</div>
										<div className="text-center">
											<div className="font-medium">
												{reflection.completed_actions_count}/
												{reflection.corrective_actions_count}
											</div>
											<div className="text-muted-foreground">Actions</div>
										</div>
									</div>

									{reflection.corrective_actions_count > 0 && (
										<div className="mt-3">
											<Progress
												value={reflection.progress_percentage}
												className="h-1"
											/>
										</div>
									)}
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-8 text-muted-foreground">
							<Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
							<p>No completed reflections yet.</p>
							<p className="text-sm mt-2">
								Encourage your mentee to create their first reflection to begin
								tracking progress.
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
